import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen, BarChart3, User, GraduationCap, Target, Compass, Coins, Clock,
  Users, Heart, Zap, ArrowRight, Info, ChevronDown, Briefcase, Sparkles,
  TrendingUp, ExternalLink, Award, Settings2, Eye, EyeOff, ArrowUp, ArrowDown,
  Minimize2, Maximize2, Layers, CheckCircle2, Circle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  BookOpen, BarChart3, User, GraduationCap, Target, Compass, Coins, Clock,
  Users, Heart, Briefcase, Sparkles, TrendingUp, Award,
};
const Icon = ({ name, ...p }) => { const C = ICONS[name] || Sparkles; return <C {...p} />; };

const CAT_COLOR = {
  experience: '#F03FAF', network: '#873CFF', career_prep: '#168BFF',
  financial: '#FFC928', academic: '#00D9FF', proof: '#1EE6A8',
};
const CAT_LABEL = {
  experience: 'Experience', network: 'Network', career_prep: 'Career Prep',
  financial: 'Financial', academic: 'Academic', proof: 'Proof',
};

const PANEL = 'rounded-2xl border p-5 sm:p-6';
const panelStyle = (glow = '#00D9FF') => ({
  background: 'linear-gradient(160deg, rgba(11,49,91,0.55), rgba(6,27,58,0.75))',
  borderColor: 'rgba(0,217,255,0.16)',
  boxShadow: `0 0 50px -26px ${glow}`,
});

const fade = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

// ── Circular progress ring (defensible: shows display value + "how") ──
const RING_ICON = { academic: 'GraduationCap', clarity: 'Compass', experience: 'Briefcase', network: 'Users', financial: 'Coins', ai_ready: 'Sparkles' };
function Ring({ d, open, onToggle }) {
  const { pct = 0, color, label, status, status_color, display, how, kind } = d;
  const R = 34, C = 2 * Math.PI * R;
  return (
    <div className="flex flex-col items-center text-center relative">
      <div className="relative w-[88px] h-[88px]">
        <svg width="88" height="88" className="-rotate-90">
          <circle cx="44" cy="44" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
          <motion.circle cx="44" cy="44" r={R} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
            strokeDasharray={C} initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: C - (C * pct) / 100 }}
            transition={{ duration: 1.1, ease: 'easeOut' }} style={{ filter: `drop-shadow(0 0 6px ${color})` }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-1">
          {kind === 'state'
            ? <Icon name={RING_ICON[d.key]} className="w-6 h-6" style={{ color }} />
            : <>
                <Icon name={RING_ICON[d.key]} className="w-3.5 h-3.5 mb-0.5" style={{ color }} />
                <span className="font-outfit font-black text-white leading-none text-lg">{display}</span>
              </>}
        </div>
      </div>
      <p className="text-[11px] text-slate-300 font-medium leading-tight mt-2 max-w-[100px]">{label}</p>
      <p className="text-[11px] font-bold mt-0.5 leading-tight max-w-[104px]" style={{ color: status_color }}>{status}</p>
      <button onClick={onToggle} data-testid={`ring-how-${d.key}`}
        className="text-[9.5px] text-slate-300 hover:text-[#00D9FF] mt-0.5 inline-flex items-center gap-0.5">
        <Info className="w-2.5 h-2.5" /> How?
      </button>
      <AnimatePresence>
        {open && how && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="absolute top-full z-30 mt-1 w-56 rounded-xl border border-cyan-400/30 bg-[#08254A] p-3 text-left shadow-xl">
            <p className="text-[10px] uppercase tracking-wider text-[#00D9FF] font-bold mb-1">How this is determined</p>
            <p className="text-[11px] text-slate-300 mb-1"><span className="text-slate-300">Rule:</span> {how.rule}</p>
            {how.inputs?.length > 0 && <ul className="text-[11px] text-slate-300 mb-1 list-disc pl-4">{how.inputs.map((x, i) => <li key={i}>{x}</li>)}</ul>}
            <p className="text-[10.5px] text-slate-300">{how.changes}</p>
            {how.updated && <p className="text-[9.5px] text-slate-300 mt-1">Updated {new Date(how.updated).toLocaleDateString()}</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function WhyPop({ items }) {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} data-testid="nbm-why"
        className="inline-flex items-center gap-1 text-[11px] text-slate-300 hover:text-white transition-colors">
        <Info className="w-3.5 h-3.5" /> Why this?
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="absolute right-0 z-20 mt-2 w-60 rounded-xl border border-cyan-400/30 bg-[#08254A] p-3 shadow-xl">
            <p className="text-[11px] text-slate-300 mb-2">We recommended this because:</p>
            <div className="flex flex-wrap gap-1.5">
              {items.map((b, i) => <span key={i} className="text-[10.5px] px-2 py-0.5 rounded-full border border-white/12 text-slate-200">{b}</span>)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function CollegeDashboardPage() {
  const { token } = useAuth();
  const [home, setHome] = useState(null);
  const [priorities, setPriorities] = useState([]);
  const [recs, setRecs] = useState([]);
  const [layout, setLayout] = useState([]);
  const [customizing, setCustomizing] = useState(false);
  const [openHow, setOpenHow] = useState(null);
  const [loading, setLoading] = useState(true);

  const authH = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API}/situation/college-home`, h),
      axios.get(`${API}/situation/priorities`, h).catch(() => ({ data: { priorities: [] } })),
      axios.get(`${API}/situation/home-layout`, h).catch(() => ({ data: { layout: [] } })),
      axios.get(`${API}/recommendations`, h).catch(() => ({ data: { recommendations: [] } })),
    ]).then(([hm, pr, ly, rc]) => {
      setHome(hm.data);
      setPriorities(pr.data.priorities || []);
      setLayout(ly.data.layout || []);
      setRecs(rc.data.recommendations || []);
    }).finally(() => setLoading(false));
  }, [token]);

  const setRecState = (rid, state) => {
    setRecs(rs => rs.map(r => r.recommendation_id === rid ? { ...r, state } : r));
    axios.post(`${API}/recommendations/${rid}/state`, { state }, authH).catch(() => {});
  };

  const persistLayout = (next) => {
    setLayout(next);
    axios.post(`${API}/situation/home-layout`, { layout: next }, authH).catch(() => {});
  };
  const toggleVisible = (id) => persistLayout(layout.map(m => m.id === id ? { ...m, visible: !m.visible } : m));
  const toggleCompact = (id) => persistLayout(layout.map(m => m.id === id ? { ...m, compact: !m.compact } : m));
  const move = (idx, dir) => {
    const next = [...layout]; const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    persistLayout(next);
  };

  if (loading) return <AppLayout><div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner size="lg" /></div></AppLayout>;
  if (!home) return <AppLayout><div className="text-center py-20 text-red-400" data-testid="college-dash-error">Failed to load your dashboard.</div></AppLayout>;

  const { hero, stats, journey, next_best_move, profile, understand, reality, resources, recommended } = home;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).toUpperCase();

  return (
    <AppLayout>
      <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.05 } } }}
        className="space-y-5 pb-10" data-testid="college-dashboard">

        {/* ═══ HERO ═══ */}
        <motion.div variants={fade} className={PANEL} style={panelStyle('#168BFF')} data-testid="dash-hero">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div>
              <p className="text-[11px] tracking-[0.22em] text-[#00D9FF] font-bold">{today}</p>
              <h1 className="font-outfit font-black text-4xl sm:text-5xl text-white mt-2 leading-none">
                Welcome back, <span style={{ background: 'linear-gradient(90deg,#00D9FF,#873CFF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{hero.name}.</span>
              </h1>
              <p className="text-lg text-slate-200 mt-3 max-w-2xl">{hero.headline}</p>
              <p className="text-sm text-slate-300 mt-1">{hero.subline}</p>
            </div>
            <div className="lg:text-right lg:pl-8 lg:border-l border-white/10 shrink-0 max-w-xs">
              <p className="text-[15px] text-slate-200 italic leading-snug">"{hero.quote}"</p>
              <p className="mt-4 font-outfit font-bold text-sm leading-tight" style={{ background: 'linear-gradient(90deg,#00D9FF,#873CFF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                MORE THAN A STUDENT.<br />A WHOLE YOU.
              </p>
            </div>
          </div>
        </motion.div>

        {/* ═══ SOMETHING CHANGED? ═══ */}
        <SomethingChanged authH={authH} onApplied={() => {
          const h = authH;
          axios.get(`${API}/situation/college-home`, h).then(r => setHome(r.data)).catch(() => {});
          axios.get(`${API}/recommendations`, h).then(r => setRecs(r.data.recommendations || [])).catch(() => {});
        }} />

        {/* ═══ STAT CARDS ═══ */}
        <motion.div variants={fade} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="dash-stats">
          {stats.map((s) => {
            const inner = (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name={s.icon} className="w-4 h-4" style={{ color: s.color }} />
                  <span className="text-[10px] uppercase tracking-wider text-slate-300 font-bold">{s.label}</span>
                </div>
                <p className="font-outfit font-bold text-white text-lg leading-tight truncate">{s.value}</p>
                {s.pct != null && (
                  <div className="h-1.5 rounded-full bg-white/8 mt-2 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${s.pct}%`, background: s.color, boxShadow: `0 0 8px ${s.color}` }} />
                  </div>
                )}
                {s.sub && <p className="text-[11px] mt-1.5" style={{ color: s.key === 'gpa' || s.key === 'grad' ? '#1EE6A8' : '#B4C4DA' }}>{s.sub}</p>}
              </>
            );
            const cls = 'rounded-2xl border p-4 transition-all';
            const st = { background: 'rgba(8,37,74,0.5)', borderColor: `${s.color}30`, boxShadow: `0 0 30px -18px ${s.color}` };
            // Header facts (major/credits/GPA/graduation) are read-only — not navigation.
            return <div key={s.key} data-testid={`stat-${s.key}`} className={cls} style={st}>{inner}</div>;
          })}
        </motion.div>

        {/* ═══ JOURNEY + NEXT BEST MOVE ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <motion.div variants={fade} className={`lg:col-span-2 ${PANEL}`} style={panelStyle('#00D9FF')} data-testid="dash-journey">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-outfit font-bold text-xl text-white">Your Journey at a Glance</h2>
            </div>
            <p className="text-[12px] text-slate-300 mb-5">Live progress across the areas that matter most.</p>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {journey.map((j) => (
                <Ring key={j.key} d={j} open={openHow === j.key} onToggle={() => setOpenHow(openHow === j.key ? null : j.key)} />
              ))}
            </div>
          </motion.div>

          <motion.div variants={fade} className="rounded-2xl border p-6 relative overflow-hidden" data-testid="dash-nbm"
            style={{ background: 'linear-gradient(160deg, rgba(135,60,255,0.18), rgba(22,139,255,0.10))', borderColor: 'rgba(135,60,255,0.4)', boxShadow: '0 0 60px -24px #873CFF' }}>
            <div aria-hidden className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full blur-3xl opacity-25" style={{ background: 'radial-gradient(circle,#873CFF,transparent 62%)' }} />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#FFC928]" style={{ filter: 'drop-shadow(0 0 6px #FFC928)' }} />
                  <span className="font-outfit font-bold text-white">Your Next Best Move</span>
                </div>
                <WhyPop items={next_best_move.why} />
              </div>
              <h3 className="font-outfit font-black text-2xl text-white leading-tight">{next_best_move.title}</h3>
              <p className="text-[13px] text-slate-300 mt-2 leading-relaxed">{next_best_move.body}</p>
              <Link to={next_best_move.link} data-testid="nbm-action"
                className="mt-5 flex items-center justify-center gap-2 w-full rounded-xl py-3 font-bold text-white transition-transform hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(90deg,#00D9FF,#873CFF)', boxShadow: '0 0 24px -6px #873CFF' }}>
                View Action Plan <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* ═══ UNDERSTAND · REALITY · PRIORITIES+RESOURCES ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* What We Understand */}
          <motion.div variants={fade} className={PANEL} style={panelStyle('#00D9FF')} data-testid="dash-understand">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-outfit font-bold text-lg text-white">What We Understand About You</h2>
              <Link to="/app/my-situation" className="text-[11px] text-[#00D9FF] hover:underline flex items-center gap-0.5" data-testid="understand-link">View My Situation <ArrowRight className="w-3 h-3" /></Link>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {understand.chips.map((c) => <span key={c} className="text-[11px] px-2.5 py-1 rounded-full border border-cyan-400/25 bg-cyan-500/[0.06] text-slate-200">{c}</span>)}
            </div>
            {understand.narrative && <p className="text-[13px] text-slate-300 leading-relaxed">{understand.narrative}</p>}
            <div className="mt-4 rounded-xl border border-cyan-400/25 bg-cyan-500/[0.05] p-3 flex gap-2">
              <span className="text-[#00D9FF] text-2xl leading-none font-serif">"</span>
              <p className="text-[12.5px] text-slate-200 italic">{understand.quote}</p>
            </div>
          </motion.div>

          {/* Where You Are Right Now */}
          <motion.div variants={fade} className={PANEL} style={panelStyle('#00D9FF')} data-testid="dash-reality">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-outfit font-bold text-lg text-white">Where You Are Right Now</h2>
              <span className="text-[10.5px] text-slate-300">From your situation</span>
            </div>
            <div className="space-y-2.5">
              {reality.map((r) => (
                <div key={r.key} className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/8 flex items-center justify-center shrink-0">
                    <Icon name={r.icon} className="w-4 h-4 text-[#00D9FF]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-white font-medium leading-tight">{r.label}</p>
                    <p className="text-[11px] text-slate-300 leading-tight truncate">{r.detail}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="w-2 h-2 rounded-full" style={{ background: r.dot, boxShadow: `0 0 6px ${r.dot}` }} />
                    <span className="text-[11px] font-semibold" style={{ color: r.dot }}>{r.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Priorities + Resources */}
          <div className="space-y-5">
            <motion.div variants={fade} className={PANEL} style={panelStyle('#00D9FF')} data-testid="dash-priorities">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-outfit font-bold text-lg text-white">Upcoming Priorities</h2>
                <Link to="/app/my-strategy" className="text-[11px] text-[#00D9FF] hover:underline flex items-center gap-0.5">View All <ArrowRight className="w-3 h-3" /></Link>
              </div>
              <PriorityList recs={recs} onState={setRecState} authH={authH} />
            </motion.div>

            <motion.div variants={fade} className={PANEL} style={panelStyle('#00D9FF')} data-testid="dash-resources">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-outfit font-bold text-lg text-white">Helpful Resources for You</h2>
              </div>
              <p className="text-[11px] text-slate-300 mb-3">Support at {home.hero.institution || 'your campus'} matched to your situation.</p>
              <div className="grid grid-cols-2 gap-2.5">
                {resources.map((r) => (
                  <div key={r.title} data-testid={`resource-${r.category}`}
                    className="rounded-xl border border-white/8 bg-white/[0.03] p-2.5">
                    <p className="text-[12px] text-white font-medium leading-tight">{r.title}</p>
                    <p className="text-[10.5px] text-slate-300 leading-snug mt-1 line-clamp-2">{r.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* ═══ RECOMMENDED FOR YOU ═══ */}
        <motion.div variants={fade} className={PANEL} style={panelStyle('#873CFF')} data-testid="dash-recommended">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-1">
            <div className="flex items-baseline gap-2">
              <h2 className="font-outfit font-bold text-xl text-white">Recommended For You</h2>
              <span className="text-[12px] text-slate-300">Based on your situation, goals, and what's happening right now.</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {recommended.map((r) => {
              const color = CAT_COLOR[r.category] || '#873CFF';
              return (
                <Link key={r.title} to={r.link} data-testid={`recommended-${r.category}`}
                  className="group rounded-xl border p-4 transition-all hover:-translate-y-0.5 flex items-start gap-3"
                  style={{ background: `${color}0D`, borderColor: `${color}33`, boxShadow: `0 0 30px -20px ${color}` }}>
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}1F` }}>
                    <Icon name={r.icon} className="w-5 h-5" style={{ color }} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13.5px] text-white font-semibold leading-tight">{r.title}</p>
                    <p className="text-[11px] text-slate-300 leading-snug mt-0.5">{r.desc}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-white group-hover:translate-x-0.5 transition-all ml-auto shrink-0" />
                </Link>
              );
            })}
          </div>
        </motion.div>

        {/* ═══ CUSTOMIZABLE MODULES (below the core dashboard) ═══ */}
        <motion.div variants={fade} data-testid="dash-customize">
          <div className="flex items-center justify-between mb-1 mt-2">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-[#00D9FF]" />
              <h2 className="font-outfit font-bold text-lg text-white">More about you</h2>
            </div>
            <button onClick={() => setCustomizing(c => !c)} data-testid="customize-toggle"
              className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border border-white/12 text-slate-300 hover:border-cyan-400/40 hover:text-[#00D9FF] transition-colors">
              <Settings2 className="w-3.5 h-3.5" /> {customizing ? 'Done' : 'Customize'}
            </button>
          </div>
          <p className="text-[12px] text-slate-300 mb-3">Optional deep-dives into the data behind your dashboard. Hide what you don't need.</p>

          {customizing && (
            <div className="rounded-2xl border border-cyan-400/20 bg-[#08254A]/50 p-4 mb-4 space-y-2" data-testid="customize-panel">
              <p className="text-[11px] text-slate-300 mb-1">Show, hide, reorder or compact your modules. Changes save automatically.</p>
              {layout.map((m, idx) => (
                <div key={m.id} className="flex items-center gap-2 rounded-lg bg-white/[0.03] px-3 py-2">
                  <button onClick={() => toggleVisible(m.id)} data-testid={`cfg-visible-${m.id}`} className="shrink-0">
                    {m.visible ? <Eye className="w-4 h-4 text-[#1EE6A8]" /> : <EyeOff className="w-4 h-4 text-slate-300" />}
                  </button>
                  <span className="text-[13px] text-white flex-1">{m.label}</span>
                  <button onClick={() => toggleCompact(m.id)} data-testid={`cfg-compact-${m.id}`} title="Compact / expanded" className="text-slate-300 hover:text-[#00D9FF]">
                    {m.compact ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`cfg-up-${m.id}`} className="text-slate-300 hover:text-white disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === layout.length - 1} data-testid={`cfg-down-${m.id}`} className="text-slate-300 hover:text-white disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {layout.filter(m => m.visible).map((m) => (
              <HomeModule key={m.id} id={m.id} compact={m.compact} profile={profile} authH={authH} />
            ))}
          </div>
        </motion.div>

      </motion.div>
    </AppLayout>
  );
}

// ── Optional dashboard modules (sourced; honest empty states) ──
function PriorityList({ recs, onState, authH }) {
  const visible = (recs || []).filter(r => !['dismissed', 'completed'].includes(r.state)).slice(0, 5);
  if (visible.length === 0) return (
    <p className="text-[12px] text-slate-300 py-2" data-testid="priorities-empty">
      No priorities right now. <Link to="/app/my-situation" className="text-[#00D9FF]">Tell us your situation →</Link>
    </p>
  );
  return <div className="space-y-2.5" data-testid="priorities-list">{visible.map(r => <PriorityRow key={r.recommendation_id} r={r} onState={onState} authH={authH} />)}</div>;
}

function PriorityRow({ r, onState, authH }) {
  const [open, setOpen] = useState(false);
  const [aiText, setAiText] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const color = CAT_COLOR[r.category] || '#168BFF';
  const d = r.deadline ? new Date(r.deadline) : null;

  const explain = () => {
    if (aiText || aiBusy) return;
    setAiBusy(true);
    axios.post(`${API}/ai/explain-recommendation`, { recommendation_id: r.recommendation_id }, authH)
      .then(res => setAiText(res.data.explanation))
      .catch(() => setAiText('Could not generate an explanation right now.'))
      .finally(() => setAiBusy(false));
  };
  return (
    <div data-testid={`priority-${r.recommendation_id}`} className="flex items-start gap-3">
      <div className="w-11 shrink-0 text-center rounded-lg py-1" style={{ background: `${color}18` }}>
        {d ? <>
          <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color }}>{d.toLocaleDateString('en-US', { month: 'short' })}</p>
          <p className="text-base font-black text-white leading-none">{d.getDate()}</p>
        </> : <Sparkles className="w-4 h-4 mx-auto my-1.5" style={{ color }} />}
      </div>
      <div className="min-w-0 flex-1 border-b border-white/6 pb-2.5">
        <div className="flex items-start justify-between gap-2">
          <Link to={r.destination} className="text-[13px] text-white font-medium leading-tight hover:text-[#00D9FF] transition-colors">{r.title}</Link>
          {r.state === 'in_progress' && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0" style={{ color: '#22E6D1', background: '#22E6D11F' }}>In progress</span>}
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[11px] text-slate-300">{d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : r.timeframe}</span>
          <span className="text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color, background: `${color}18` }}>{CAT_LABEL[r.category] || r.origin_module}</span>
        </div>
        <div className="flex items-center gap-3 mt-1.5">
          <button onClick={() => setOpen(o => !o)} data-testid={`priority-why-${r.recommendation_id}`} className="text-[10.5px] text-slate-300 hover:text-[#00D9FF] inline-flex items-center gap-0.5"><Info className="w-3 h-3" /> Why this?</button>
          {r.state !== 'in_progress' && <button onClick={() => onState(r.recommendation_id, 'in_progress')} data-testid={`priority-start-${r.recommendation_id}`} className="text-[10.5px] text-[#1EE6A8] hover:underline">Start</button>}
          <button onClick={() => onState(r.recommendation_id, 'dismissed')} data-testid={`priority-dismiss-${r.recommendation_id}`} className="text-[10.5px] text-slate-300 hover:text-[#FF3D7F]">Dismiss</button>
        </div>
        <AnimatePresence>
          {open && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="mt-1.5 rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
                <p className="text-[11px] text-slate-300 leading-snug">{r.explanation || r.reason}</p>
                {r.inputs?.length > 0 && <div className="flex flex-wrap gap-1 mt-1.5">{r.inputs.map((b, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/12 text-slate-300">{b}</span>)}</div>}
                <p className="text-[10px] text-slate-300 mt-1.5">Source: {r.origin_module}{r.reason_codes?.length ? ` · ${r.reason_codes.join(', ')}` : ''}</p>
                {aiText
                  ? <p className="text-[11.5px] text-cyan-100 mt-2 pt-2 border-t border-white/8 leading-snug"><Sparkles className="w-3 h-3 inline mr-1 text-[#00D9FF]" />{aiText}</p>
                  : <button onClick={explain} data-testid={`priority-ai-${r.recommendation_id}`} className="text-[10.5px] text-[#00D9FF] hover:underline mt-2 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />{aiBusy ? 'Thinking…' : 'Explain with AI'}</button>}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const SCENARIO_OPTIONS = [
  'Take a paid internship this term',
  'Add a minor in Data Science',
  'Add a certification (e.g., SQL)',
  'Delay graduation one semester',
  'Reduce work hours to focus on a project',
  'Do an unpaid research assistantship',
];

function StrategySimulator({ authH }) {
  const [picked, setPicked] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = (s) => setPicked(p => p.includes(s) ? p.filter(x => x !== s) : (p.length >= 3 ? p : [...p, s]));
  const compare = () => {
    if (picked.length < 2) return;
    setBusy(true); setResult(null);
    axios.post(`${API}/ai/compare-scenarios`, { scenarios: picked }, authH)
      .then(r => setResult(r.data))
      .catch(() => setResult({ comparison: 'Could not compare right now. Try again.' }))
      .finally(() => setBusy(false));
  };

  return (
    <ModuleShell title="Strategy Simulator" subtitle="Compare 2-3 paths across time, cost & readiness. We won't pick a 'best' for you." color="#873CFF" testid="module-strategy">
      <div className="flex flex-wrap gap-2 mb-3">
        {SCENARIO_OPTIONS.map(s => {
          const on = picked.includes(s);
          return (
            <button key={s} onClick={() => toggle(s)} data-testid={`scenario-${s.slice(0, 10)}`}
              className="text-[11.5px] px-2.5 py-1.5 rounded-full border transition-colors"
              style={{ borderColor: on ? '#873CFF' : 'rgba(255,255,255,0.12)', background: on ? 'rgba(135,60,255,0.15)' : 'transparent', color: on ? '#c4b5fd' : '#94a3b8' }}>
              {on ? '✓ ' : ''}{s}
            </button>
          );
        })}
      </div>
      <button onClick={compare} disabled={picked.length < 2 || busy} data-testid="scenario-compare"
        className="text-[13px] font-bold text-white rounded-lg px-4 py-2 disabled:opacity-40"
        style={{ background: 'linear-gradient(90deg,#00D9FF,#873CFF)' }}>
        {busy ? 'Comparing…' : `Compare ${picked.length || ''} scenarios`}
      </button>
      {picked.length < 2 && <span className="text-[11px] text-slate-300 ml-2">Pick 2-3 to compare.</span>}
      {result && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-3 rounded-xl border border-purple-400/25 bg-purple-500/[0.06] p-3" data-testid="scenario-result">
          <p className="text-[10px] uppercase tracking-wider text-[#c4b5fd] font-bold mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Comparison</p>
          <p className="text-[12.5px] text-slate-100 leading-relaxed whitespace-pre-line">{result.comparison}</p>
          {result.grounded_on && <p className="text-[9.5px] text-slate-300 mt-2">Grounded on your {result.grounded_on.finances ? 'finances' : ''}{result.grounded_on.finances && result.grounded_on.career ? ' & ' : ''}{result.grounded_on.career ? 'career data' : ''}.</p>}
        </motion.div>
      )}
    </ModuleShell>
  );
}

function ModuleShell({ title, subtitle, color = '#00D9FF', children, testid }) {
  return (
    <div className="rounded-2xl border p-5" style={panelStyle(color)} data-testid={testid}>
      <h3 className="font-outfit font-bold text-white">{title}</h3>
      {subtitle && <p className="text-[11px] text-slate-300 mb-3 mt-0.5">{subtitle}</p>}
      {!subtitle && <div className="mb-3" />}
      {children}
    </div>
  );
}

function HomeModule({ id, compact, profile, authH }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (id === 'profile' || id === 'strategy') return; // profile uses props; strategy is interactive
    const url = id === 'opportunities' ? `${API}/opportunities/ranked`
      : id === 'proof_blueprint' ? `${API}/proof/blueprint`
      : id === 'finances' ? `${API}/finances/feasibility`
      : id === 'career' ? `${API}/career/target-intel`
      : id === 'experience_gap' ? `${API}/experience/gap`
      : `${API}/situation/home-module/${id}`;
    axios.get(url, authH).then(r => setData(r.data)).catch(() => setData({ empty: true }));
  }, [id]); // eslint-disable-line

  if (id === 'strategy') return <StrategySimulator authH={authH} />;
  if (id === 'profile') {
    return (
      <ModuleShell title="Your FuturePath Profile" subtitle="A snapshot of everything FuturePath understands about you." testid="module-profile">
        <p className="text-[11px] text-slate-300 mb-3">{profile.complete} of {profile.total} context areas understood</p>
        <div className="space-y-2">
          <ProfileLine label="Who You Are" value={profile.who} missing={profile.missing.includes('self')} link="/app/assessment" />
          <ProfileLine label="Your Reality" value={(profile.reality || []).join(' • ')} missing={profile.missing.includes('situation')} link="/app/my-situation" />
          <ProfileLine label="What You Want" value={profile.want} missing={profile.missing.includes('lifestyle')} link="/app/calculator" />
          <ProfileLine label="Where You're Headed" value={profile.headed} missing={profile.missing.includes('academic') && profile.missing.includes('direction')} link="/app/career-intelligence" />
        </div>
      </ModuleShell>
    );
  }
  if (!data) return <ModuleShell title="…" testid={`module-${id}`}><div className="h-16 animate-pulse bg-white/5 rounded" /></ModuleShell>;

  if (id === 'proof') return (
    <ModuleShell title="Experience & Proof" subtitle="Projects, internships and skills you've recorded as evidence." color="#F03FAF" testid="module-proof">
      {data.empty ? <EmptyState text="No proof or experience recorded yet." link={data.link} cta="Start building proof" />
        : <div className="grid grid-cols-3 gap-2">{data.stats.map((s, i) => (
            <Link key={i} to={data.link} className="rounded-xl border border-white/8 bg-white/[0.03] p-3 text-center hover:border-cyan-400/30">
              <p className="font-outfit font-black text-xl" style={{ color: '#F03FAF' }}>{s.value}</p>
              <p className="text-[10px] text-slate-300 leading-tight mt-1">{s.label}</p>
            </Link>))}</div>}
    </ModuleShell>
  );
  if (id === 'financial') return (
    <ModuleShell title="Financial Snapshot" subtitle="How affordability is shaping your recommendations." color="#FFC928" testid="module-financial">
      {data.empty ? <EmptyState text="Tell us your finances in My Situation." link="/app/my-situation" cta="Update situation" />
        : <div><Link to={data.link} className="inline-block"><span className="text-lg font-outfit font-bold" style={{ color: '#FFC928' }}>{data.status}</span></Link>
            <p className="text-[12px] text-slate-300 mt-1">{data.detail}</p></div>}
    </ModuleShell>
  );
  if (id === 'network') return (
    <ModuleShell title="Network & Relationships" subtitle="Professional relationships you've recorded in your field." color="#873CFF" testid="module-network">
      {data.empty ? <EmptyState text={data.detail} link={data.link} cta="Add connections" />
        : <p className="text-[13px] text-slate-200">{data.detail}</p>}
    </ModuleShell>
  );
  if (id === 'recent') return (
    <ModuleShell title="Recently Updated" subtitle="The latest changes to your FuturePath data." testid="module-recent">
      {data.empty ? <EmptyState text="Nothing updated yet." />
        : <ul className="space-y-1.5">{data.items.map((it, i) => (
            <li key={i} className="flex items-center justify-between text-[12px]">
              <span className="text-slate-200">{it.label}</span>
              <span className="text-slate-300">{it.at ? new Date(it.at).toLocaleDateString() : ''}</span>
            </li>))}</ul>}
    </ModuleShell>
  );
  if (id === 'opportunities') return (
    <ModuleShell title="Opportunities for You" subtitle="Real records ranked against your situation, never invented." color="#F03FAF" testid="module-opportunities">
      {data.empty ? <EmptyState text={data.note || 'No matching opportunities yet.'} link="/app/internships" cta="Browse opportunities" />
        : <div className="space-y-2">{(data.opportunities || []).slice(0, 3).map((o) => (
            <Link key={o.id} to={`/app/internships?opp=${o.id}`} data-testid={`opp-${o.id}`} className="block rounded-xl border border-white/8 bg-white/[0.03] p-3 hover:border-cyan-400/30">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[13px] text-white font-medium leading-tight">{o.title}</p>
                {o.paid && <span className="text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ color: '#1EE6A8', background: '#1EE6A81F' }}>Paid</span>}
              </div>
              <p className="text-[11px] text-slate-300 mt-0.5">{o.provider}{o.deadline ? ` · due ${o.deadline}` : ''}</p>
              <p className="text-[10.5px] text-cyan-200/80 mt-1">{(o.match_inputs || []).slice(0, 2).join(' · ')}</p>
            </Link>))}</div>}
    </ModuleShell>
  );
  if (id === 'proof_blueprint') return (
    <ModuleShell title="Proof Blueprint" subtitle={`What ${data.target_career || 'your target role'} needs vs what you've shown.`} color="#873CFF" testid="module-proof-blueprint">
      {data.empty ? <EmptyState text="Set a career target to see your blueprint." link="/app/career-intelligence" cta="Set target" />
        : <div>
            <p className="text-[11px] text-slate-300 mb-2">{data.covered_count} of {data.total} evidence areas covered</p>
            <ul className="space-y-1.5">
              {data.areas.map((area, i) => {
                const done = (data.covered || []).includes(area);
                return (<li key={i} className="flex items-start gap-2 text-[12.5px]">
                  {done ? <CheckCircle2 className="w-4 h-4 text-[#1EE6A8] mt-0.5 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />}
                  <span className={done ? 'text-slate-300' : 'text-white'}>{area}</span>
                </li>);
              })}
            </ul>
          </div>}
    </ModuleShell>
  );
  if (id === 'experience_gap') return (
    <ModuleShell title="Skill Gap" subtitle={`Skills ${data.role || 'your target role'} needs vs what you've shown.`} color="#F03FAF" testid="module-experience-gap">
      {data.empty ? <EmptyState text={data.note || 'Set a target to map your skills.'} link="/app/career-intelligence" cta="Set target" />
        : <div>
            <p className="text-[11px] text-slate-300 mb-2">{data.covered_count} of {data.total} required skills shown</p>
            <div className="space-y-1.5">
              {(data.have || []).map((s, i) => (
                <div key={`h${i}`} className="flex items-center gap-2 text-[12.5px]">
                  <CheckCircle2 className="w-4 h-4 text-[#1EE6A8] shrink-0" /><span className="text-slate-300">{s.name}</span>
                </div>))}
              {(data.gaps || []).slice(0, 5).map((s, i) => (
                <div key={`g${i}`} className="flex items-start gap-2 text-[12.5px]">
                  <Circle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-white">{s.name}</span>
                    <span className="text-[9.5px] font-bold uppercase ml-1.5 px-1 py-0.5 rounded" style={{ color: s.importance === 'critical' ? '#FF3D7F' : s.importance === 'high' ? '#FFC928' : '#C6D4E8', background: 'rgba(255,255,255,0.05)' }}>{s.importance}</span>
                  </div>
                </div>))}
            </div>
          </div>}
    </ModuleShell>
  );
  if (id === 'finances') return (
    <ModuleShell title="Financial Snapshot" subtitle="Real affordability math from your Design My Life plan." color="#FFC928" testid="module-finances">
      {data.empty ? <EmptyState text={data.note || 'Complete Design My Life to see affordability.'} link="/app/calculator" cta="Design My Life" />
        : <div>
            <p className="text-[13.5px] text-slate-100 leading-snug">{data.summary}</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div className="rounded-xl border border-white/8 bg-white/[0.03] p-2.5">
                <p className="text-[10px] uppercase tracking-wider text-slate-300 font-bold">Lifestyle needs</p>
                <p className="font-outfit font-black text-lg" style={{ color: '#FFC928' }}>${(data.gross_income_required || 0).toLocaleString()}<span className="text-[11px] text-slate-300 font-normal">/yr</span></p>
              </div>
              {data.expected_starting_income != null && (
                <div className="rounded-xl border border-white/8 bg-white/[0.03] p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-300 font-bold">Typical pay</p>
                  <p className="font-outfit font-black text-lg" style={{ color: data.feasible ? '#1EE6A8' : '#FF3D7F' }}>${(data.expected_starting_income || 0).toLocaleString()}<span className="text-[11px] text-slate-300 font-normal">/yr</span></p>
                </div>
              )}
            </div>
            {data.tradeoffs?.length > 0 && <ul className="mt-2 space-y-1">{data.tradeoffs.map((t, i) => <li key={i} className="text-[11.5px] text-slate-300 flex items-start gap-1.5"><ArrowRight className="w-3 h-3 mt-0.5 text-[#FFC928] shrink-0" />{t}</li>)}</ul>}
            {data.alerts?.map((al, i) => <p key={i} className="text-[11px] text-amber-300/90 mt-2">⚠ {al}</p>)}
          </div>}
    </ModuleShell>
  );
  if (id === 'career') return (
    <ModuleShell title="Career Outlook" subtitle={`Real labor-market data for ${data.role || 'your target'}.`} color="#168BFF" testid="module-career">
      {data.empty ? <EmptyState text={data.note || 'Set a career target to see market data.'} link="/app/career-intelligence" cta="Set target" />
        : <div className="space-y-2">
            {data.salary && <div className="flex items-center justify-between">
              <span className="text-[12px] text-slate-300">Typical pay</span>
              <span className="text-[13px] text-white font-semibold">{data.salary.median || data.salary.range}</span>
            </div>}
            {data.education && <div className="flex items-center justify-between">
              <span className="text-[12px] text-slate-300">Entry education</span>
              <span className="text-[13px] text-white font-semibold capitalize">{data.education}</span>
            </div>}
            {data.ai && (data.ai.augmentation_potential != null) && <div className="flex items-center justify-between">
              <span className="text-[12px] text-slate-300">AI augmentation</span>
              <span className="text-[13px] font-semibold" style={{ color: '#168BFF' }}>{data.ai.augmentation_potential}%</span>
            </div>}
            {data.required_skills?.length > 0 && <div className="pt-1">
              <p className="text-[10px] uppercase tracking-wider text-slate-300 font-bold mb-1">Key required skills</p>
              <div className="flex flex-wrap gap-1">{data.required_skills.slice(0, 5).map((s, i) => <span key={i} className="text-[10.5px] px-1.5 py-0.5 rounded-full border border-blue-400/25 text-slate-200">{s.name || s}</span>)}</div>
            </div>}
            {data.sources?.length > 0 && <p className="text-[9.5px] text-slate-300 pt-1">Source: {data.sources.map(s => `${s.source}${s.as_of ? ` (${s.as_of})` : ''}`).join(' · ')}</p>}
          </div>}
    </ModuleShell>
  );
  return null;
}

function ProfileLine({ label, value, missing, link }) {
  return (
    <div className="flex items-start gap-2">
      {missing ? <Circle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-[#1EE6A8] mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-slate-300 font-bold">{label}</p>
        {missing || !value
          ? <Link to={link} className="text-[12.5px] text-[#00D9FF] hover:underline">Add this →</Link>
          : <p className="text-[13px] text-slate-100 leading-snug">{value}</p>}
      </div>
    </div>
  );
}

function EmptyState({ text, link, cta }) {
  return (
    <div className="text-center py-4">
      <p className="text-[12.5px] text-slate-300">{text}</p>
      {link && cta && <Link to={link} className="text-[12px] text-[#00D9FF] hover:underline mt-1 inline-block">{cta} →</Link>}
    </div>
  );
}

const CHANGE_FIELDS = {
  financial: { label: 'My finances', options: [['comfortable', 'Comfortable'], ['manageable', 'Manageable'], ['concerned', 'Concerned'], ['affecting', 'Affecting my decisions'], ['at_risk', 'At risk']] },
  employment: { label: 'My work hours', options: [['none', 'Not working'], ['lt15', 'Under 15 hrs/week'], ['15_29', '15-29 hrs/week'], ['30plus', '30+ hrs/week'], ['fulltime', 'Full-time']] },
  career_clarity: { label: 'My career direction', options: [['specific', 'I have a specific target'], ['several', 'A few possibilities'], ['general', 'A general area'], ['unsure', 'Unsure'], ['explore', 'Still exploring']] },
  belonging: { label: 'My campus connection', options: [['strong', 'Strongly connected'], ['somewhat', 'Somewhat connected'], ['not_very', 'Not very connected'], ['not_yet', 'Not yet connected']] },
};

function SomethingChanged({ authH, onApplied }) {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState('financial');
  const [value, setValue] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);

  const interpret = () => {
    if (!note.trim() || noteBusy) return;
    setNoteBusy(true); setSuggestions(null);
    axios.post(`${API}/ai/situation-note`, { text: note }, authH)
      .then(r => setSuggestions(r.data.interpretation?.suggestions || []))
      .catch(() => setSuggestions([]))
      .finally(() => setNoteBusy(false));
  };
  const applySuggestion = (s) => {
    if (CHANGE_FIELDS[s.field]) { setField(s.field); setValue(s.value); }
  };

  const apply = () => {
    if (!value) return;
    setBusy(true);
    axios.post(`${API}/context/changed`, { field, value }, authH)
      .then(r => { setResult(r.data); onApplied && onApplied(); })
      .catch(() => setResult({ summary: 'Could not apply that change. Try again.' }))
      .finally(() => setBusy(false));
  };

  return (
    <div className="rounded-2xl border p-4" style={panelStyle('#873CFF')} data-testid="something-changed">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#873CFF]" />
          <span className="text-[13px] text-white font-medium">Something changed in your life?</span>
          <span className="text-[12px] text-slate-300 hidden sm:inline">Update it and we'll re-check your strategy.</span>
        </div>
        <button onClick={() => { setOpen(o => !o); setResult(null); }} data-testid="changed-toggle"
          className="text-[12px] px-3 py-1.5 rounded-full border border-white/12 text-slate-300 hover:border-purple-400/50 hover:text-[#a78bfa] transition-colors">
          {open ? 'Close' : 'Update something'}
        </button>
      </div>

      {open && (
        <>
          {/* Free-text → AI interpretation (suggestions only) */}
          <div className="mt-3 rounded-xl border border-purple-400/20 bg-purple-500/[0.05] p-3" data-testid="changed-freetext">
            <label className="text-[11px] text-slate-300 flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#a78bfa]" /> Or tell us in your own words</label>
            <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
              <input value={note} onChange={e => setNote(e.target.value)} data-testid="changed-note-input"
                placeholder="e.g. I just picked up more hours at work and money's tight"
                className="flex-1 bg-[#08254A] border border-white/12 rounded-lg px-3 py-2 text-[13px] text-white placeholder:text-slate-300" />
              <button onClick={interpret} disabled={!note.trim() || noteBusy} data-testid="changed-note-interpret"
                className="text-[12px] font-bold text-white rounded-lg px-4 py-2 disabled:opacity-40 whitespace-nowrap"
                style={{ background: 'linear-gradient(90deg,#873CFF,#00D9FF)' }}>{noteBusy ? 'Reading…' : 'Interpret'}</button>
            </div>
            {suggestions && (
              suggestions.length === 0
                ? <p className="text-[11px] text-slate-300 mt-2" data-testid="changed-note-none">Couldn't map that to a known signal, pick a field below instead.</p>
                : <div className="mt-2 space-y-1.5" data-testid="changed-note-suggestions">
                    <p className="text-[11px] text-slate-300">Tap a suggestion to fill it in (nothing changes until you Apply):</p>
                    {suggestions.map((s, i) => (
                      <button key={i} onClick={() => applySuggestion(s)} data-testid={`changed-note-sugg-${i}`}
                        className="block w-full text-left rounded-lg border border-cyan-400/25 bg-cyan-500/[0.06] px-3 py-1.5 hover:border-cyan-400/50 transition-colors">
                        <span className="text-[12px] text-white font-medium">{(CHANGE_FIELDS[s.field]?.label) || s.field}: {(CHANGE_FIELDS[s.field]?.options.find(([k]) => k === s.value)?.[1]) || s.value}</span>
                        {s.why && <span className="block text-[11px] text-slate-300">{s.why}</span>}
                      </button>
                    ))}
                  </div>
            )}
          </div>

          <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-end">
            <label className="text-[11px] text-slate-300">What changed?
              <select value={field} onChange={e => { setField(e.target.value); setValue(''); }} data-testid="changed-field"
                className="block mt-1 bg-[#08254A] border border-white/12 rounded-lg px-2 py-1.5 text-[13px] text-white">
                {Object.entries(CHANGE_FIELDS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </label>
            <label className="text-[11px] text-slate-300">New answer
              <select value={value} onChange={e => setValue(e.target.value)} data-testid="changed-value"
                className="block mt-1 bg-[#08254A] border border-white/12 rounded-lg px-2 py-1.5 text-[13px] text-white min-w-[180px]">
                <option value="">Choose…</option>
                {CHANGE_FIELDS[field].options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <button onClick={apply} disabled={!value || busy} data-testid="changed-apply"
              className="text-[13px] font-bold text-white rounded-lg px-4 py-2 disabled:opacity-40"
              style={{ background: 'linear-gradient(90deg,#00D9FF,#873CFF)' }}>{busy ? 'Re-checking…' : 'Apply & recalculate'}</button>
          </div>
        </>
      )}

      {result && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-3 rounded-xl border border-purple-400/25 bg-purple-500/[0.06] p-3" data-testid="changed-result">
          <p className="text-[13px] text-white font-medium">{result.summary}</p>
          {result.affects?.length > 0 && <p className="text-[12px] text-slate-300 mt-1"><span className="text-slate-300">What it affects:</span> {result.affects.join(', ')}</p>}
          {result.recommend_changes?.length > 0 && <p className="text-[12px] text-cyan-200/90 mt-1"><span className="text-slate-300">New recommendations:</span> {result.recommend_changes.join(', ')}</p>}
          {result.no_change_needed?.length > 0 && <p className="text-[11px] text-slate-300 mt-1">Unchanged: {result.no_change_needed.join(', ')}</p>}
        </motion.div>
      )}
    </div>
  );
}
